import React, { useEffect, useState, useContext, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import Collapsible from 'react-collapsible';
import { PDFDownloadLink } from "@react-pdf/renderer";

import { ReactComponent as DownloadIcon } from '../assets/download-icon.svg';
import { ReactComponent as DeleteIcon } from '../assets/delete-icon.svg';
import { ReactComponent as CTSmsIcon } from '../assets/sms.svg';
import { ReactComponent as CTemailIcon } from '../assets/email.svg';
import { ReactComponent as CTPhoneIcon } from '../assets/phone.svg';

import OwnerContext from '../context/OwnerContext';
import { isOwnerStatusValid, ownerLogOut } from '../helpers/AuthOwnerHelper';
import { OwnerHttpServiceAuth } from '../services/ApiServices';
import { ErrorDanish } from '../helpers/ErrorDanish';
import Spinner from '../components/Spinner';
import OwnerFooter from '../components/OwnerFooter';
import OwnerLinksBar from '../components/ownerConsents/OwnerLinksBar';
import OwnerConsentsAdvertiserBar from '../components/ownerConsents/OwnerConsentsAdvertiserBar';
import OwnerConsent from '../components/ownerConsents/OwnerConsent';
import MainErrorText from '../components/MainErrorText';
import OwnerRevokeReasonModal from '../components/OwnerRevokeReasonModal';
import OwnerConsentsPdf from '../components/ownerConsents/OwnerConsentsPdf';
import { PhoneNumberSpacing } from '../helpers/TextCheckers';
import Disclaimer from '../components/Disclaimer';


function OwnerConsentsPage() {

    const ownerLoginObject = useContext(OwnerContext);
    const history = useHistory();
    const [ consentsRequested, setConsentsRequested ] = useState<boolean>(false);
    const [ spinner, showSpinner ] = useState<boolean>(true);
    const [ advertisersConsentsCollection, setAdvertisersConsentsCollection ] = useState<any | null>(null);
    const [ errorText, setErrorText ] = useState<string>('');
    const [ searchText, setSearchText ] = useState<string>('');
    const [ numberRevoked, setNumberRevoked ] = useState<number | null>(null);
    const [ revokeCollectionId, setRevokeCollectionId ] = useState<number | null>(null);
    const [ isRevokeButtonsDisabled, setIsRevokeButtonsDisabled ] = useState<boolean>(true);
    const [ isExportButtonsDisabled, setIsExportButtonsDisabled ] = useState<boolean>(true);
    const [ readyForPdf, setReadyForPdf ] = useState<boolean>(false);
    
    const consentTypeImages = [  <CTSmsIcon />, <CTemailIcon />, <CTPhoneIcon /> ];


    useEffect(() => {
        if (!consentsRequested) {
            // Check ownerStatus exists and is not expired.
            if (!isOwnerStatusValid(ownerLoginObject)) {
                history.push('/');
            }

            if (ownerLoginObject !== null && ownerLoginObject.ownerStatus !== null) {
                showSpinner(true);
                setConsentsRequested(true);

                OwnerHttpServiceAuth(ownerLoginObject, 'owners/consents', 'GET')
                .then(result => {
                    // No result returned.
                    if (result === undefined || result === null) {
                        setErrorText(ErrorDanish["GeneralError"]);
                    } else {
                        switch(result.status) {
                            case 200: // Created
                            result.json()
                            .then(resultJson => {
                                // Generic write error.
                                if (resultJson === undefined || resultJson === null) {
                                    setErrorText(ErrorDanish["DbFetchDataError"]);
                                // Success.
                                } else {
                                    setAdvertisersConsentsCollection(resultJson);
                                    setErrorText('');
                                    setIsRevokeButtonsDisabled(false);
                                    setIsExportButtonsDisabled(false);
                                    setTimeout(() => { setReadyForPdf(true) }, 1);
                                }
                            });
                            break;
                            case 400:   // Bad Request
                                setErrorText(ErrorDanish['DbFetchDataError']);
                                break;
                            case 401:   // Unauthorized
                            case 403:   // Forbidden
                                ownerLogOut(ownerLoginObject.setOwnerStatus);
                                history.push('/');
                                break;
                            default:    // General write error
                                setErrorText(ErrorDanish['DbFetchDataError']);
                                break;
                        }
                    }

                    showSpinner(false);
                })
                .catch(e => {
                    console.log("Something fishy happened: " + e);
                    setErrorText(ErrorDanish["DbConxError"]);
                    showSpinner(false);
                });
            }
        }
    }, [ ownerLoginObject, history, consentsRequested, setAdvertisersConsentsCollection ]);

    
    function revokeConsents(advertiserId: number, allAdvertisers: boolean = false) {
        showSpinner(true);
        setErrorText('');
        setIsRevokeButtonsDisabled(true);

        OwnerHttpServiceAuth(ownerLoginObject, 'owners/revoke', 'POST', { advertiserId:advertiserId, byOwnerViaLink: 0, allAdvertisers: allAdvertisers })
        .then(result => {
            if (result === null || result === undefined) {
                setErrorText(ErrorDanish["GeneralError"]);
            } else {
                switch(result.status) {
                    case 200: // OK
                        result.json()
                        .then(resultJson => {
                            // Generic write error.
                            if (resultJson === undefined || resultJson === null) {
                                setErrorText(ErrorDanish["DbGeneralWriteError"]);
                            // Success.
                            } else {
                                setNumberRevoked(resultJson.consentsRevoked);
                                setRevokeCollectionId(resultJson.revokeCollectionId);
                                setAdvertisersConsentsCollection(null);
                            }
                        });
                        break;
                    case 400:   // Bad Request
                    default:    // General error
                        setErrorText(ErrorDanish['DbGeneralWriteError']);
                    break;
                }
            }

            setIsRevokeButtonsDisabled(false);
            showSpinner(false);
        })
        .catch(e => {
            console.log("Something fishy happened: " + e);
            setNumberRevoked(null);
            setErrorText(ErrorDanish["DbConxError"]);
            setIsRevokeButtonsDisabled(false);
            showSpinner(false);
        });
    }


    function closeRevokeReasonModal() {
        setNumberRevoked(null);
        setConsentsRequested(false);
        setIsRevokeButtonsDisabled(false);
    }
 

    return (
        <>
            <OwnerLinksBar />
            <div className="content-container">
                <div className="page-top d-md-flex justify-content-between">
                    { spinner && <Spinner /> }
                    { numberRevoked !== null && revokeCollectionId !== null &&
                        <OwnerRevokeReasonModal setIsPopoverOpen={ closeRevokeReasonModal } revokeCollectionId={ revokeCollectionId } revokedCount={ numberRevoked } /> }
                    <div>
                        <h1>Dine samtykker</h1>
                        <p>Herunder kan du se og tilbagekalde dine markedsføringssamtykker fra vores samarbejdspartnere</p>
                    </div>
                    <div className="btn-container align-right">
                        { readyForPdf && advertisersConsentsCollection !== null && advertisersConsentsCollection.advertisersWithConsents.length > 0 && !isExportButtonsDisabled &&
                            <PDFDownloadLink
                                document={<OwnerConsentsPdf advertisersWithConsents={advertisersConsentsCollection.advertisersWithConsents} />}
                                fileName="Samtykker.pdf"
                                className="btn-transparent"
                            >
                                {({ loading }) =>
                                    loading ? "Henter dokument..." : <button className="btn btn-transparent btn-w-icon"><DownloadIcon />Exporter til PDF</button>
                                }
                            </PDFDownloadLink>
                        }
                        { (advertisersConsentsCollection !== null && advertisersConsentsCollection.existsUnrevoked) &&
                            <button className="btn btn-w-icon btn-delete" disabled={isRevokeButtonsDisabled}
                                onClick={() => { revokeConsents(0, true)}}><DeleteIcon />Tilbagekald alle</button>
                        }
                    </div>
                </div>

                <div className="search-field">
                    <input id="seachField" type="search" defaultValue={searchText}
                        onInput={ (e: ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value) } />
                </div>

                <div className="seperator"></div>

                { errorText !== '' && <MainErrorText errorText={errorText} /> }

                { advertisersConsentsCollection !== null &&
                    <div className="before-table d-md-flex">
                        <div>
                            <p>Samtykker tilknyttet</p>
                            <h2>{ PhoneNumberSpacing(advertisersConsentsCollection.mobileOrEmailSearchText) }</h2>
                        </div>
                    </div>
                }

                { advertisersConsentsCollection !== null && advertisersConsentsCollection.advertisersWithConsents.length > 0 ?
                    <div className="consent-list">
                        { advertisersConsentsCollection.advertisersWithConsents.filter((adv: any) => adv.stakeholderName.toLowerCase().startsWith(searchText.toLowerCase()))
                            .map((adv: any, index: number) => (
                            <div key={index} className="item">
                                <Collapsible key={index} open={ false }
                                    triggerClassName={'top d-flex align-items-center justify-content-between accordion' + ( !adv.hasUnrevoked && ' revoked') }
                                    transitionTime={400} easing={'ease-in-out'}
                                    triggerOpenedClassName="top d-flex align-items-center justify-content-between accordion active"
                                    triggerTagName='div'
                                    trigger={
                                        <OwnerConsentsAdvertiserBar advertiserName={adv.stakeholderName} advertiserId={Number(adv.advertiserId)}
                                            advertiserLogo={adv.advertiserLogoName} clickRevoke={revokeConsents}
                                            isRevokeButtonsDisabled={isRevokeButtonsDisabled} showRevoke={ adv.hasUnrevoked } />
                                        }>
                                    { adv.consents.map((consent: any, indexInner: number) => (
                                        <OwnerConsent key={indexInner} consent={consent} consentTypeImages={consentTypeImages} />
                                        ))
                                    }
                                </Collapsible>
                            </div>
                        ))}
                    </div>
                :
                    advertisersConsentsCollection !== null && advertisersConsentsCollection.advertisersWithConsents.length === 0 &&
                        <h3 className="content-center">Der blev ingen samtykker fundet</h3>
                }

                <br />
                <Disclaimer />

            </div>
            <OwnerFooter />
        </>
    )
}

export default OwnerConsentsPage
